import { ILatLng } from "polydraw";
import { distinctUntilChanged, Observable } from "rxjs";

export function deepDistinctUntilChanged() {
    return function <T>(source: Observable<T>) {
        return source.pipe(distinctUntilChanged((prev, next) => CompareUtil.deepEquals(prev, next)));
    };
}
export class CompareUtil {
    static arrayEquals(a: any[], b: any[]) {
        if (a === b) return true;
        if (a == null || b == null) return a === b;
        if (a.length !== b.length) return false;

        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false;
        }
        return true;
    }

    static nullUndefinedZeroEquals(a: number | string, b: number | string) {
        // 0 == '0' == null = undefined
        return a == b || (a == 0 && b == null) || (a == null && b == 0);
    }

    static deepEquals(x: any, y: any): boolean {
        //Hentet fra denne diskusjonen: https://stackoverflow.com/a/44649659
        if (x === y) {
            return true; // if both x and y are null or undefined and exactly the same
        } else if (!(x instanceof Object) || !(y instanceof Object)) {
            return false; // if they are not strictly equal, they both need to be Objects
        } else if (x.constructor !== y.constructor) {
            // they must have the exact same prototype chain, the closest we can do is
            // test their constructor.
            return false;
        } else {
            for (const p in x) {
                if (!Object.prototype.hasOwnProperty.call(x, p)) {
                    continue; // other properties were tested using x.constructor === y.constructor
                }
                if (!Object.prototype.hasOwnProperty.call(y, p)) {
                    return false; // allows to compare x[ p ] and y[ p ] when set to undefined
                }
                if (x[p] === y[p]) {
                    continue; // if they have the same strict value or identity then they are equal
                }
                if (typeof x[p] !== "object") {
                    return false; // Numbers, Strings, Functions, Booleans must be strictly equal
                }
                if (!CompareUtil.deepEquals(x[p], y[p])) {
                    return false;
                }
            }
            for (const p in y) {
                if (Object.prototype.hasOwnProperty.call(y, p) && !Object.prototype.hasOwnProperty.call(x, p)) {
                    return false;
                }
            }
            return true;
        }
    }
    
    // Compare two polygon arrays, with option to ignore checking final element in each polygon due to Leaflet vs GeoBorder closed differences
    static polygonsEqual(a: ILatLng[][][], b: ILatLng[][][], ignoreClosed = true): boolean {
        const comparePolygon = (x: ILatLng[], y: ILatLng[]) => {
            const isClosed = x[0].lat === x[x.length - 1].lat && x[0].lng === x[x.length - 1].lng;
            const length = ignoreClosed && (isClosed ? x.length - 1 : x.length);
            for (let i = 0; i < length; i++) {
                if (x[i].lat !== y[i].lat || x[i].lng !== y[i].lng) {
                    return false;
                }
            }
            return true;
        };
        if (a === b) {
            return true;
        }
        if (a == null || b == null) {
            return a === b;
        }
        if (a.length !== b.length) {
            return false;
        }
        for (let i = 0; i < a.length; ++i) {
            for (let j = 0; j < a[i].length; j++) {
                if (!comparePolygon(a[i][j], b[i][j])) {
                    return false;
                }
            }
        }
        return true;
    }
}
